import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Clapperboard, Music, Hand, Sparkles, Plus, Pencil, X } from 'lucide-react'
import { playlistThumbUrl, layoutThumbUrl } from '@/lib/playlistThumb'
import { TRANSITIONS } from '@/lib/transitions'
import PlaylistContentPicker from '@/components/PlaylistContentPicker'
import TransitionPickerDialog from '@/components/TransitionPickerDialog'
import { TransitionPreview } from '@/components/TransitionPreview'
import { useMixCatalogs } from '@/lib/mixCatalogs'

// A Mix's full ingredient set — main content (playlist OR layout) plus the
// same companions a playlist/layout can carry inline.
export type MixIngredientsValue = {
  playlistId: number | null
  layoutId: number | null
  audioId: number | null
  audioPlaylistId: number | null
  stopScreenId: number | null
  transition: string | null
}

// Ingredients editor for a Mix — unlike CompanionSection (a tab switcher used
// by the playlist/layout create dialogs, one active picker at a time), a mix
// shows every ingredient as its own always-visible CARD with a thumbnail, like
// a video editor's tracks: Main Content, Audio, Stop Screen, Transition. The
// four cards stretch to fill the ingredients panel. Picking a card opens a
// real visual content-selection modal (thumbnail grid, not a plain text
// list) for that content type; a mix only ever holds one item per row.
export default function MixIngredients({
  value, onChange,
}: {
  value: MixIngredientsValue
  onChange: (next: MixIngredientsValue) => void
}) {
  const { t } = useTranslation()
  const { allMedia, contentGroups, audioGroups, stopScreenGroups, resolvePlaylist, resolveLayout, resolveAudio, resolveAudioPlaylist, resolveStopScreen } = useMixCatalogs()

  const [contentOpen, setContentOpen] = useState(false)
  const [audioOpen, setAudioOpen] = useState(false)
  const [stopScreenOpen, setStopScreenOpen] = useState(false)
  const [transitionOpen, setTransitionOpen] = useState(false)

  // ── Main content (playlist OR layout) ──────────────────────────────────
  const contentValue = value.playlistId != null
    ? `playlist:${value.playlistId}`
    : value.layoutId != null ? `layout:${value.layoutId}` : null
  const setContentValue = (v: string | null) => {
    if (v?.startsWith('playlist:')) onChange({ ...value, playlistId: Number(v.slice(9)), layoutId: null })
    else if (v?.startsWith('layout:')) onChange({ ...value, playlistId: null, layoutId: Number(v.slice(7)) })
    else onChange({ ...value, playlistId: null, layoutId: null })
  }
  const selectedPlaylist = value.playlistId != null ? resolvePlaylist(value.playlistId) : undefined
  const selectedLayout = value.layoutId != null ? resolveLayout(value.layoutId) : undefined
  const contentLabel = value.playlistId != null
    ? selectedPlaylist?.name ?? t('mix.playlist', 'Playlist')
    : value.layoutId != null
      ? selectedLayout?.name ?? t('mix.layout', 'Layout')
      : null
  const contentThumb = selectedPlaylist
    ? playlistThumbUrl(selectedPlaylist.media)
    : selectedLayout ? layoutThumbUrl(selectedLayout.zones, allMedia) : null

  // ── Audio ───────────────────────────────────────────────────────────────
  const audioValue = value.audioId != null
    ? `file:${value.audioId}`
    : value.audioPlaylistId != null ? `playlist:${value.audioPlaylistId}` : null
  const setAudioValue = (v: string | null) => {
    if (v?.startsWith('file:')) onChange({ ...value, audioId: Number(v.slice(5)), audioPlaylistId: null })
    else if (v?.startsWith('playlist:')) onChange({ ...value, audioId: null, audioPlaylistId: Number(v.slice(9)) })
    else onChange({ ...value, audioId: null, audioPlaylistId: null })
  }
  const audioLabel = value.audioId != null
    ? resolveAudio(value.audioId)?.original_name ?? t('companions.audio', 'Audio')
    : value.audioPlaylistId != null
      ? resolveAudioPlaylist(value.audioPlaylistId)?.name ?? t('companions.audioPlaylist', 'Audio playlist')
      : null

  // ── Stop screen ─────────────────────────────────────────────────────────
  const selectedStopScreen = value.stopScreenId != null ? resolveStopScreen(value.stopScreenId) : undefined
  const stopScreenLabel = value.stopScreenId != null
    ? selectedStopScreen?.name ?? t('companions.stopScreen', 'Stop screen')
    : null
  const stopScreenThumb = selectedStopScreen ? playlistThumbUrl(selectedStopScreen.media) : null

  // ── Transition ──────────────────────────────────────────────────────────
  const transitionLabel = value.transition
    ? TRANSITIONS.find(tr => tr.key === value.transition)?.name ?? value.transition
    : null
  const transitionThumbNode = value.transition
    ? <TransitionPreview anim={`tr-${value.transition}`} className="w-full h-full" logoClass="h-5" />
    : null

  return (
    <div className="flex flex-col gap-3 h-full">
      <IngredientCard
        icon={Clapperboard}
        label={t('mix.mainContent', 'Main content')}
        value={contentLabel}
        thumb={contentThumb}
        hint={t('mix.mainContentHint', 'A playlist or layout — the visual content this mix publishes')}
        colorClass="bg-white dark:bg-neutral-900 text-muted-foreground"
        onAdd={() => setContentOpen(true)}
        onClear={() => setContentValue(null)}
      />
      <IngredientCard
        icon={Music}
        label={t('companions.audio', 'Audio')}
        value={audioLabel}
        colorClass="bg-white dark:bg-neutral-900 text-muted-foreground"
        onAdd={() => setAudioOpen(true)}
        onClear={() => setAudioValue(null)}
      />
      <IngredientCard
        icon={Hand}
        label={t('companions.stopScreen', 'Stop screen')}
        value={stopScreenLabel}
        thumb={stopScreenThumb}
        colorClass="bg-white dark:bg-neutral-900 text-muted-foreground"
        onAdd={() => setStopScreenOpen(true)}
        onClear={() => onChange({ ...value, stopScreenId: null })}
      />
      <IngredientCard
        icon={Sparkles}
        label={t('companions.transition', 'Transition')}
        value={transitionLabel}
        thumbNode={transitionThumbNode}
        colorClass="bg-white dark:bg-neutral-900 text-muted-foreground"
        onAdd={() => setTransitionOpen(true)}
        onClear={() => onChange({ ...value, transition: null })}
      />

      {/* Ingredient pickers — the same PlaylistContentPicker the Playlist/
          Layout content tabs use, in its single-select "custom groups" mode
          (see the type's doc comment in PlaylistContentPicker.tsx) rather
          than a separate bespoke picker component. */}
      {contentOpen && (
        <PlaylistContentPicker
          open
          customGroups={contentGroups}
          customSelected={contentValue}
          onCustomSelect={setContentValue}
          title={t('mix.mainContent', 'Main content')}
          onClose={() => setContentOpen(false)}
          onConfirm={() => {}}
        />
      )}
      {audioOpen && (
        <PlaylistContentPicker
          open
          customGroups={audioGroups}
          customSelected={audioValue}
          onCustomSelect={setAudioValue}
          title={t('companions.audio', 'Audio')}
          onClose={() => setAudioOpen(false)}
          onConfirm={() => {}}
        />
      )}
      {stopScreenOpen && (
        <PlaylistContentPicker
          open
          customGroups={stopScreenGroups}
          customSelected={value.stopScreenId != null ? `stopScreen:${value.stopScreenId}` : null}
          onCustomSelect={(v) => onChange({ ...value, stopScreenId: v != null ? Number(v.slice(11)) : null })}
          title={t('companions.stopScreen', 'Stop screen')}
          onClose={() => setStopScreenOpen(false)}
          onConfirm={() => {}}
        />
      )}
      <TransitionPickerDialog
        open={transitionOpen}
        selected={value.transition}
        onSelect={(key) => onChange({ ...value, transition: key })}
        onClose={() => setTransitionOpen(false)}
      />
    </div>
  )
}

// One ingredient as a card: a thumbnail (or icon tile when there's nothing
// renderable — audio has no visual thumbnail at all) beside the label/value
// and its edit/clear controls. Cards flex-1 so the four of them stretch to
// fill the ingredients panel instead of clumping at the top. The edit button
// shows a pencil once the card has content, a plus otherwise.
function IngredientCard({
  icon: Icon, label, value, thumb, thumbNode, hint, colorClass, onAdd, onClear,
}: {
  icon: typeof Music
  label: string
  value: string | null
  /** Resolved thumbnail image URL, when the content has one. */
  thumb?: string | null
  /** A live preview node (used for Transition) — takes priority over `thumb`. */
  thumbNode?: ReactNode
  hint?: string
  colorClass: string
  onAdd: () => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const hasValue = value != null
  return (
    <div className="flex-1 min-h-[152px] flex items-stretch rounded-xl border border-border overflow-hidden bg-card hover:border-primary/40 transition-colors">
      <button
        type="button"
        onClick={onAdd}
        aria-label={hasValue ? t('common.edit', 'Edit') : t('companions.add', 'Add')}
        className={`relative w-40 sm:w-52 shrink-0 overflow-hidden flex items-center justify-center ${thumb || thumbNode ? 'bg-muted' : colorClass}`}
      >
        {thumbNode ?? (thumb
          ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
          : <Icon className="w-11 h-11" />)}
      </button>

      <div className="flex-1 min-w-0 flex items-center justify-between gap-3 px-5">
        <button type="button" onClick={onAdd} className="min-w-0 flex-1 text-start py-2">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5" />
            {label}
          </p>
          <p className={`text-base truncate mt-1 ${hasValue ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
            {hasValue ? value : (hint ?? t('companions.tapToChoose', 'Tap to choose'))}
          </p>
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onAdd}
            aria-label={hasValue ? t('common.edit', 'Edit') : t('companions.add', 'Add')}
            className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors"
          >
            {hasValue ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
          {hasValue && (
            <button
              type="button"
              onClick={onClear}
              aria-label={t('companions.clear', 'Remove')}
              className="h-10 w-10 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
